/**
 * GenExpensesService
 *
 * This service handles all general expense-related operations in the application.
 * It provides methods for managing general expenses, including creation, retrieval,
 * updating, and deletion of general expense entries. The service supports operations
 * for both individual matters and system-wide general expense management.
 *
 * @class
 */

import HttpService from './HttpService';
import {
  pathGenExpenses,
  pathGenExpensesByMatter,
  pathGenExpenseById,
} from '@frontend/__settings__/septSoftwarePaths';

const http = new HttpService();

class GenExpensesService {
  /**
   * Retrieves all general expenses from the system
   *
   * @returns {Promise<Array>} Array of general expense objects
   * @throws {Error} If the API request fails
   */
  static async getAllGenExpenses(): Promise<any[]> {
    const data = await http.get(`${pathGenExpenses}`);
    return data || [];
  }

  /**
   * Retrieves general expenses for a specific matter
   *
   * @param {string} matterId - ID of the matter
   * @returns {Promise<Array>} Array of general expenses for the matter
   * @throws {Error} If the API request fails
   */
  static async getGenExpensesByMatter(matterId: string): Promise<any[]> {
    const data = await http.get(`${pathGenExpensesByMatter(matterId)}`);
    return data;
  }

  /**
   * Adds a new general expense
   *
   * @param {Object} expenseData - General expense data
   * @param {string} expenseData.matterId - ID of the matter
   * @param {string} expenseData.name - Name of the expense
   * @param {number} expenseData.amount - Amount of the expense
   * @param {string} [expenseData.description] - Description of the expense
   *
   * @returns {Promise<Object>} Created general expense object
   * @throws {Error} If the API request fails
   */
  static async addGenExpense(expenseData: any): Promise<any> {
    const data = await http.post(`${pathGenExpenses}`, expenseData);
    return data;
  }

  /**
   * Updates an existing general expense
   *
   * @param {string} geId - ID of the general expense
   * @param {Object} expenseData - Updated general expense data
   * @returns {Promise<Object>} Updated general expense object
   * @throws {Error} If the API request fails
   */
  static async updateGenExpense(geId: string, expenseData: any): Promise<any> {
    const data = await http.put(`${pathGenExpenseById(geId)}`, expenseData);
    return data;
  }

  /**
   * Deletes a general expense
   *
   * @param {string} geId - ID of the general expense to delete
   * @returns {Promise<Object>} Response indicating success or failure
   * @throws {Error} If the API request fails
   */
  static async deleteGenExpense(geId: string): Promise<any> {
    const data = await http.delete(`${pathGenExpenseById(geId)}`);
    return data;
  }
}

export default GenExpensesService;
